import { Helmet } from "react-helmet-async";
import { Row, Col, message, Tabs, Card, Button, Empty, Table, Modal, Input } from "antd";
import TopHeaderBlock from "./components/TopHeaderBlock";
import LeftInformationPanel from "./components/LeftInformationPanel";
import WorkHistoryPanel, { type CareFocus } from "./components/WorkHistoryPanel";
import Layout from '../../Layout'
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { LeftOutlined } from "@ant-design/icons";
import { Phone, UserPlus, MessageSquareText, UserX } from "lucide-react";
import { CustomerDetailData } from "../../api/ChannelCustomer";
import { mixGetV1 } from "../../services/mixin";
import useAuthStore from "../../store/authStore";
import CustomerProfile from "./components/CustomerProfile";
import InvoiceHistory from "./components/InvoiceHistory";
import HistoryList from "./components/HistoryList";
import CustomerTaskPanel from "./components/CustomerTaskPanel";
import CustomerJourney from "./components/CustomerJourney";
import AppointmentModal from "../CustomerDemo/components/AppointmentModal";
import {
    CS_STAGES, DNC_REASON_IDS, FAIL_REASONS, UNASSIGNED, callCenterApi, callKind, callsOf, customerById, customerByPhone, lastContactAt,
    fmtDuration, isValidPhone, primaryPhone, samePhone, onStorageError, useCallCenter, visiblePhotos, type Customer, type HistoryInput, type State,
} from "../CustomerDemo/callCenterStore";
import { meName, softphone } from "../CustomerDemo/softphone";
import { fileToDataUrl } from "../CustomerDemo/imageUtil";
import Softphone from "../CustomerDemo/components/Softphone";
import CreateCustomerModal from "../CustomerDemo/components/CreateCustomerModal";
import { KindBadge, QuickNotesModal } from "../CustomerDemo/components/CallWidgets";

type Opt = { label: string; value: any; color?: string };

const FIELD_LABEL: Record<string, string> = {
    display_name: "Tên khách hàng",
    phone: "Số điện thoại",
    email: "Email",
    birthday: "Ngày sinh",
    gender: "Giới tính",
    note: "Ghi chú",
    customer_occupation_id: "Nghề nghiệp",
    customer_care_status_id: "Tag chat",
    cs_stage: "Trạng thái CS",
    staff_id: "Nhân viên trực page",
    telesale_id: "Nhân viên Telesale",
    last_call_result: "Báo cuộc gọi lỗi",
    source: "Nguồn khách",
    referrer: "Người giới thiệu",
    medical_note: "Tiền sử / Dị ứng",
};

/** Khách hàng trong kho → dạng dữ liệu mà các component chi tiết (vốn dùng API) đang đọc */
/** Hỏi lý do (bắt buộc) trước khi thực hiện thay đổi nhạy cảm. Trả về null nếu huỷ */
function askReason(title: string, detail: string): Promise<string | null> {
    return new Promise(resolve => {
        let reason = "";
        const m = Modal.confirm({
            title,
            content: (
                <div className="space-y-2 pt-1">
                    <div className="text-sm text-gray-500">{detail}</div>
                    <Input.TextArea autoFocus autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Lý do thay đổi (bắt buộc)"
                        onChange={e => { reason = e.target.value; m.update({ okButtonProps: { disabled: !reason.trim() } }); }} />
                </div>
            ),
            okText: "Xác nhận",
            cancelText: "Huỷ",
            okButtonProps: { disabled: true },
            onOk: () => resolve(reason.trim() || null),
            onCancel: () => resolve(null),
        });
    });
}

function toDetailData(c: Customer, s: State): CustomerDetailData {
    // "Liên hệ gần nhất" dùng chung định nghĩa với phần tổng quan 360 (cuộc gọi nghe máy / chăm sóc qua kênh)
    const lastTouch = lastContactAt(s, c.phones);
    const hasAgent = c.agent && c.agent !== UNASSIGNED;
    return {
        id: c.id,
        customer_code: `KH${String(c.id).padStart(5, "0")}`,
        display_name: c.name,
        phone: primaryPhone(c),
        email: c.email || null,
        birthday: c.birthday || null,
        gender: c.gender ?? null,
        avatar: c.avatar || null,
        address: c.address || null,
        province_code: c.provinceCode || null,
        ward_code: c.wardCode || null,
        // Nhân viên chưa gắn id thật (dữ liệu mẫu) → dùng khoá theo tên để ô chọn vẫn hiển thị đúng người
        staff_id: (c.staffId ?? (hasAgent ? `name:${c.agent}` : null)) as any,
        staff: hasAgent ? { id: c.staffId ?? 0, name: c.agent } : null,
        telesale_id: c.telesaleId ?? null,
        telesale: c.telesaleName ? { id: c.telesaleId ?? 0, name: c.telesaleName } : null,
        customer_tag_id: null,
        customer_occupation_id: c.occupationId ?? null,
        customer_care_status_id: c.careStatusId ?? null,
        note: c.note || null,
        created_at: new Date(c.createdAt).toISOString(),
        updated_at: new Date(c.createdAt).toISOString(),
        deleted_at: null,
        connections: [],
        connection: { display_name: "Tổng đài" },
        last_seen_at: lastTouch ? new Date(lastTouch).toISOString() : null,
        call_result_options: FAIL_REASONS,
        last_call_result: c.lastCallResult ?? null,
        cs_stage: c.csStage,
        source: c.source || "",
        referrer: c.referrer || "",
        medical_note: c.medicalNote || "",
    } as any;
}

/** Số máy lạ: chưa có hồ sơ → xem lịch sử gọi + tạo khách hàng */
function UnknownNumberView({ phone, onCreated }: { phone: string; onCreated: (id: number) => void }) {
    const s = useCallCenter();
    const [creating, setCreating] = useState<string | null>(null);
    const [notes, setNotes] = useState<string | null>(null);
    const calls = callsOf(s, [phone]);
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
                <Link to="/customer-demo"><LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" /></Link>
                <Link to="/customer-demo">Tổng đài</Link>
                <span className="text-gray-400">/</span>
                <span>{phone}</span>
            </div>
            <div className="bg-white dark:bg-[#1f2937] border border-gray-200 dark:border-gray-700 rounded-lg p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500"><UserX size={24} /></div>
                    <div>
                        <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{phone}</div>
                        <div className="text-sm text-gray-500">Số máy lạ — chưa có trong danh sách khách hàng · {calls.length} cuộc gọi</div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button icon={<Phone size={14} />} className="!bg-green-500 !text-white !border-green-500 hover:!bg-green-600" onClick={() => softphone.dial(phone)}>Gọi điện</Button>
                    <Button icon={<MessageSquareText size={14} />} onClick={() => setNotes(phone)}>Ghi chú</Button>
                    <Button type="primary" icon={<UserPlus size={14} />} onClick={() => setCreating(phone)}>Tạo khách hàng</Button>
                </div>
            </div>
            <Card title="Lịch sử cuộc gọi" className="dark:bg-[#141414] dark:border-[#303030]">
                <Table rowKey="id" dataSource={calls} pagination={false} size="small" scroll={{ x: "max-content" }}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có cuộc gọi" /> }}
                    columns={[
                        { title: "Thời gian", key: "t", render: (_, c) => dayjs(c.startedAt).format("HH:mm DD/MM/YYYY") },
                        { title: "Cuộc gọi", key: "k", render: (_, c) => <KindBadge kind={callKind(c)} /> },
                        { title: "Thời lượng", key: "d", render: (_, c) => (c.duration ? fmtDuration(c.duration) : "—") },
                        { title: "Nhân viên", dataIndex: "agent" },
                        { title: "Nội dung", key: "n", render: (_, c) => c.note || "—" },
                    ]} />
            </Card>
            <CreateCustomerModal phone={creating} onClose={() => setCreating(null)} onCreated={onCreated} />
            <QuickNotesModal phone={notes} onClose={() => setNotes(null)} />
        </div>
    );
}

export default function CustomerDetails() {
    const s = useCallCenter();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { isSuperAdmin } = useAuthStore();
    // Phân công (trực page / telesale) chỉ Super Admin được đổi
    const canEditAssignment = isSuperAdmin;
    const [staffOptions, setStaffOptions] = useState<Opt[]>([]);
    const [telesaleOptions, setTelesaleOptions] = useState<Opt[]>([]);
    const [customerTags, setCustomerTags] = useState<any[]>([]);
    const [careStatuses, setCareStatuses] = useState<Opt[]>([]);
    const [occupations, setOccupations] = useState<Opt[]>([]);
    const [activeTab, setActiveTab] = useState("1");
    const [booking, setBooking] = useState(false);

    const idParam = Number(params.get("id")) || null;
    // ?survey=<lịch hẹn> (từ việc Khảo sát ở danh sách) → mở thẳng tab Khảo sát CSAT
    const surveyParam = params.get("survey");
    const [careFocus, setCareFocus] = useState<CareFocus | null>(null);
    useEffect(() => {
        if (surveyParam === null) return;
        setActiveTab("2");
        setCareFocus(f => ({ tab: "khao-sat", appointmentId: surveyParam || undefined, n: (f?.n || 0) + 1 }));
        const next = new URLSearchParams(params);
        next.delete("survey");
        setParams(next, { replace: true });
    }, [surveyParam, params, setParams]);
    // Bộ nhớ trình duyệt đầy → báo ngay thay vì âm thầm mất dữ liệu
    useEffect(() => onStorageError(() => message.error("Bộ nhớ trình duyệt đã đầy — thay đổi vừa rồi KHÔNG được lưu. Xoá bớt ảnh hoặc chờ nối API.", 6)), []);
    const phoneParam = params.get("phone");
    const customer = (idParam ? customerById(s, idParam) : null) || (phoneParam ? customerByPhone(s, phoneParam) : null);
    const data = useMemo(() => (customer ? toDetailData(customer, s) : null), [customer, s]);

    // Mở bằng ?phone= mà số đã thuộc một khách → chuẩn hoá URL về ?id=
    useEffect(() => {
        if (customer && !idParam) setParams({ id: String(customer.id) }, { replace: true });
    }, [customer, idParam, setParams]);

    const fetchOptions = useCallback(async () => {
        try {
            const [staffRes, telesaleRes, tagRes, careRes, occRes] = await Promise.all([
                mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 2 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 3 }).catch(() => null),
                mixGetV1('/api/v1/be/customer-tags', { limit: 100, sort: ["id,desc"], group: 'group_1' }).catch(() => null),
                mixGetV1('/api/v1/be/customer-care-statuses', { take: 100 }).catch(() => null),
                mixGetV1('/api/v1/be/customer-occupations', { take: 100 }).catch(() => null),
            ]);
            if (staffRes?.status && staffRes.data?.items) setStaffOptions(staffRes.data.items.map((x: any) => ({ label: x.name, value: x.id })));
            if (telesaleRes?.status && telesaleRes.data?.items) setTelesaleOptions(telesaleRes.data.items.map((x: any) => ({ label: x.name, value: x.id })));
            if (tagRes?.status && tagRes.data?.items) setCustomerTags(tagRes.data.items.map((x: any) => ({ label: x.name, value: x.id, color: x.color })));
            if (careRes?.status && careRes.data?.items) setCareStatuses(careRes.data.items.map((x: any) => ({ label: x.name, value: x.id, color: x.color })));
            if (occRes?.status && occRes.data?.items) setOccupations(occRes.data.items.map((x: any) => ({ label: x.name, value: x.id })));
        } catch (e) {
            console.error("Failed to fetch options:", e);
        }
    }, []);

    useEffect(() => { fetchOptions(); }, [fetchOptions]);

    const labelOf = (opts: Opt[], v: any) => opts.find(o => String(o.value) === String(v))?.label;

    /** Giá trị hiển thị trong "Lịch sử cập nhật" */
    const display = (field: string, v: any): string => {
        if (v === null || v === undefined || v === "") return "—";
        switch (field) {
            case "gender": return ({ 1: "Nam", 2: "Nữ", 0: "Khác" } as Record<number, string>)[Number(v)] || "—";
            case "birthday": return dayjs(v).format("DD/MM/YYYY");
            case "customer_occupation_id": return labelOf(occupations, v) || `#${v}`;
            case "customer_care_status_id": return labelOf(careStatuses, v) || `#${v}`;
            case "cs_stage": return CS_STAGES.find(x => x.value === v)?.label || String(v);
            case "staff_id": return labelOf(staffOptions, v) || String(v).replace(/^name:/, "");
            case "telesale_id": return labelOf(telesaleOptions, v) || `#${v}`;
            case "last_call_result": return FAIL_REASONS.find(x => x.id === Number(v))?.text || String(v);
            default: return String(v);
        }
    };

    const handleUpdateField = async (field: string, value: any) => {
        if (!customer || !data) return;
        if ((field === 'staff_id' || field === 'telesale_id') && !canEditAssignment) {
            message.warning('Chỉ Super Admin mới được thay đổi phân công');
            return;
        }
        let patch: Partial<Customer> = {};
        const change: HistoryInput = { label: FIELD_LABEL[field] || field, from: display(field, (data as any)[field]), to: display(field, value) };
        switch (field) {
            case "display_name":
                if (!String(value || "").trim()) { message.error("Tên khách hàng không được để trống"); return; }
                patch = { name: String(value).trim() };
                break;
            case "phone": {
                const v = String(value || "").trim();
                if (!isValidPhone(v)) { message.error("Số điện thoại không hợp lệ"); return; }
                const owner = customerByPhone(s, v);
                if (owner && owner.id !== customer.id) { message.error(`Số này đã thuộc khách ${owner.name}`); return; }
                // Giữ số cũ làm số phụ để lịch sử cuộc gọi cũ vẫn gắn với khách
                patch = { phones: [v, ...customer.phones.filter(p => !samePhone(p, v))] };
                const old = primaryPhone(customer);
                if (old && !samePhone(old, v)) change.note = `Số cũ ${old} được giữ làm số phụ`;
                break;
            }
            case "staff_id":
            case "telesale_id": {
                if (change.from === change.to) return;
                // Đổi phân công ảnh hưởng hoa hồng / KPI → bắt buộc lý do
                const reason = await askReason(`Đổi ${FIELD_LABEL[field]}`, `${change.from} → ${change.to}`);
                if (!reason) return;
                change.note = reason;
                change.category = "assign";
                patch = field === "staff_id"
                    ? { staffId: value ?? null, agent: value ? labelOf(staffOptions, value) || customer.agent : UNASSIGNED }
                    : { telesaleId: value ?? null, telesaleName: value ? labelOf(telesaleOptions, value) : undefined };
                break;
            }
            case "email": patch = { email: value }; break;
            case "birthday": patch = { birthday: value }; break;
            case "gender": patch = { gender: value === "" || value === null ? null : Number(value) }; break;
            case "note": patch = { note: value }; break;
            case "customer_occupation_id": patch = { occupationId: value ?? null }; break;
            case "customer_care_status_id": patch = { careStatusId: value ?? null }; break;
            case "cs_stage": patch = { csStage: value }; break;
            case "last_call_result": patch = { lastCallResult: value }; break;
            case "source": patch = { source: value }; break;
            case "referrer": patch = { referrer: String(value || "").trim() }; break;
            case "medical_note": patch = { medicalNote: String(value || "").trim() }; break;
            default: return;
        }
        callCenterApi.updateCustomer(customer.id, patch, [change], meName());
        if (field !== "last_call_result") { message.success("Cập nhật thành công"); return; }

        // Báo cuộc gọi lỗi: ghi vào hành trình chăm sóc; lý do "sai số"/"khách không muốn nghe" → đề xuất chặn gọi
        const reason = display(field, value);
        callCenterApi.addCare(customer.phones[0], "care", `Báo cuộc gọi lỗi: ${reason}`, meName());
        message.success("Đã báo cuộc gọi lỗi");
        if (DNC_REASON_IDS.includes(Number(value)) && !customer.doNotCall) {
            Modal.confirm({
                title: "Chặn gọi khách này?",
                content: `Lý do "${reason}". Khi chặn, tổng đài sẽ không cho gọi ra và khách không xuất hiện trong danh sách "Cần gọi lại". Có thể gỡ chặn bất cứ lúc nào.`,
                okText: "Chặn gọi",
                okButtonProps: { danger: true },
                cancelText: "Không chặn",
                onOk: () => { callCenterApi.setDoNotCall(customer.id, reason, meName()); message.success("Đã chặn gọi khách này"); },
            });
        }
    };

    const handleUpdateAddress = async (v: { province_code: string; ward_code: string; address: string }) => {
        if (!customer) return;
        const areaChanged = (v.province_code || "") !== (customer.provinceCode || "") || (v.ward_code || "") !== (customer.wardCode || "");
        // Đổi tỉnh/phường mà giữ nguyên số nhà vẫn phải ghi lại
        const from = customer.address || "—";
        const to = v.address || "—";
        callCenterApi.updateCustomer(customer.id, { address: v.address, provinceCode: v.province_code, wardCode: v.ward_code },
            [{ label: "Địa chỉ", from, to: from === to && areaChanged ? `${to} (đổi Tỉnh/Phường)` : to, ...(areaChanged ? { note: "Có thay đổi Tỉnh/Thành hoặc Phường/Xã" } : {}) }], meName());
        message.success("Cập nhật thành công");
    };

    const handleUploadAvatar = async (file: File) => {
        if (!customer) return;
        const url = await fileToDataUrl(file, 256);
        callCenterApi.updateCustomer(customer.id, { avatar: url }, [{ label: "Ảnh đại diện", from: customer.avatar ? "Ảnh cũ" : "—", to: "Ảnh mới" }], meName());
        message.success("Cập nhật ảnh đại diện thành công");
    };

    const handleAddPhotos = async (files: File[]) => {
        if (!customer) return;
        const urls = await Promise.all(files.map(f => fileToDataUrl(f)));
        callCenterApi.addPhotos(customer.id, urls, { tag: "other", by: meName() });
        message.success(`Đã tải lên ${urls.length} ảnh`);
    };

    /** Xoá ảnh: chỉ người tải lên (hoặc quản lý) — ảnh cũ chưa rõ người tải thì cho xoá */
    const handleDeletePhoto = async (pid: string) => {
        if (!customer) return;
        const p = customer.photos.find(x => x.id === pid);
        if (p?.by && p.by !== meName() && !canEditAssignment) { message.warning(`Chỉ ${p.by} (người tải ảnh) hoặc quản lý mới được xoá`); return; }
        // Xoá mềm có lý do — ảnh vẫn lưu vết, quản lý khôi phục được ở tab Hình ảnh
        const reason = await askReason("Xoá ảnh", "Ảnh được ẩn khỏi hồ sơ nhưng vẫn lưu vết.");
        if (!reason) return;
        const err = callCenterApi.removePhoto(customer.id, pid, meName(), reason);
        if (err) { message.warning(err); return; }
        message.success("Đã xóa hình ảnh");
    };

    if (!customer || !data) {
        return (
            <Layout>
                <Helmet><title>Chi tiết cuộc gọi | Khothi</title></Helmet>
                {phoneParam ? (
                    <UnknownNumberView phone={phoneParam} onCreated={id => setParams({ id: String(id) }, { replace: true })} />
                ) : (
                    <div className="py-20 text-center">
                        <Empty description="Không tìm thấy khách hàng" />
                        <Button className="mt-4" onClick={() => navigate("/customer-demo")}>← Về Tổng đài</Button>
                    </div>
                )}
                <Softphone />
            </Layout>
        );
    }

    const call = () => softphone.dial(primaryPhone(customer));

    return (
        <Layout>
            <Helmet>
                <title>{customer.name} | Khothi</title>
            </Helmet>
            <div className="space-y-4">
                <TopHeaderBlock
                    customer={data}
                    staffOptions={staffOptions}
                    telesaleOptions={telesaleOptions}
                    canEditAssignment={canEditAssignment}
                    onUpdateField={handleUpdateField}
                    onUploadAvatar={handleUploadAvatar}
                    onCall={call}
                    onBook={() => setBooking(true)}
                    extraPhones={customer.phones.slice(1)}
                    onCallPhone={(p) => softphone.dial(p)}
                />

                <CustomerJourney customer={customer} onBook={() => setBooking(true)} onOpenCare={() => setActiveTab("2")} />

                <CustomerTaskPanel customer={customer} onOpenInvoices={() => setActiveTab("3")} />

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={8} className="sticky-sidebar">
                        <LeftInformationPanel
                            customer={data}
                            customerTags={customerTags}
                            careStatuses={careStatuses}
                            occupations={occupations}
                            onUpdateField={handleUpdateField}
                            onUpdateAddress={handleUpdateAddress}
                            photos={visiblePhotos(customer)}
                            onAddPhotos={handleAddPhotos}
                            onDeletePhoto={handleDeletePhoto}
                        />
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={24} xl={16}>
                        <Card className="dark:bg-[#141414] dark:border-[#303030]">
                            <Tabs
                                className="dark:[&_.ant-tabs-tab]:text-gray-400
                                        dark:[&_.ant-tabs-tab-active]:text-yellow-400
                                        dark:[&_.ant-tabs-ink-bar]:bg-yellow-400"
                                activeKey={activeTab}
                                onChange={(key) => setActiveTab(key)}
                                items={[
                                    { key: "1", label: "Tổng quan", children: <CustomerProfile customer={customer} onCall={call} /> },
                                    { key: "2", label: "Lịch sử công việc", children: <WorkHistoryPanel customer={customer} onCall={call} isManager={canEditAssignment} focus={careFocus} /> },
                                    { key: "3", label: "Lịch sử hóa đơn", children: <InvoiceHistory customer={customer} /> },
                                    { key: "4", label: "Lịch sử cập nhật", children: <HistoryList customer={customer} /> },
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
            <Softphone />
            <AppointmentModal customer={customer} open={booking} onClose={() => setBooking(false)} />
        </Layout>
    );
}
